import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnInit,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UiThemeService } from '../../lib/ui-theme.service';

export type appNavItem = {
  route?: string[];
  href?: string;
  label: string;
  icon?: string;
  items?: appNavItem[];
  highlightForChildren?: boolean;
  requirements?: {
    permissions?: string[];
    featureFlags?: string[];
    customValidator?(context): boolean;
  };
  keywords?: string[];
  hidden?: boolean;
};

@Component({
  selector: 'am-app-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app-nav.component.html',
  styleUrls: ['./app-nav.component.scss'],
})
export class AppNavComponent implements OnInit, AfterViewInit {
  constructor(
    @Inject(DOCUMENT) private document: any,
    private renderer: Renderer2,
    private theme: UiThemeService,
    public cd: ChangeDetectorRef
  ) {
    this.theme.init(renderer, document);
  }

  darkThemeEnabled: boolean;
  @Input() showMenu = false;
  @Input() appMenu: TemplateRef<any>;
  @Input() navItems: appNavItem[];
  @Input() hiddenItems: appNavItem[] = [];
  @Input() fixed = true;

  @ViewChild('navItemWrapperEl') navItemWrapperEl: ElementRef;
  @ViewChild('shownNavItemsEl') shownNavItemsEl: ElementRef;
  @ViewChild('hiddenNavItemsEl') hiddenNavItemsEl: ElementRef;
  @ViewChildren('navItemEl', { read: ElementRef })
  navItemEls!: QueryList<ElementRef>;

  resize$ = new Subject<number>();
  showMore = false;

  @HostListener('window:resize', ['$event.target'])
  onResize(target) {
    this.resize$.next(target.innerWidth);
  }

  ngOnInit() {
    if (this.fixed) {
      this.renderer.addClass(document.body, 'app-nav-fixed');
    }
  }

  ngAfterViewInit() {
    this.adaptNavItems();

    this.navItemEls.changes.subscribe(() => {
      this.adaptNavItems();
    });

    this.resize$.pipe(debounceTime(100)).subscribe((x) => {
      this.adaptNavItems();
    });
  }

  public adaptNavItems() {
    if (!this.navItemEls || !this.navItemEls.length) {
      return;
    }

    const wrapperWidth = this.navItemWrapperEl.nativeElement.offsetWidth;
    const hiddenNavWidth = 200;
    const totalSpace = wrapperWidth - hiddenNavWidth;
    let takenSpace = 0;

    this.showMore = false;
    this.navItems.forEach((n) => {
      n.hidden = false;
    });
    this.hiddenItems = [];

    this.navItemEls.forEach((i) => {
      i.nativeElement.classList.remove('d-none');
      if (!i.nativeElement) {
        return;
      }
      const itemWidth = i.nativeElement.offsetWidth;
      takenSpace += itemWidth;
      if (takenSpace > totalSpace) {
        this.showMore = true;
        const toHide = this.navItems[Number(i.nativeElement.dataset.index)];
        if (toHide) {
          this.hiddenItems.push(toHide);
        }

        i.nativeElement.classList.add('d-none');
      }
    });

    this.cd.detectChanges();
  }
}
